import { useEffect, useState } from 'react';
import { api, ApiError } from '../lib/api';
import { finishSignIn, idToken, openRestaurant, signOut, startSignIn } from '../lib/owner';
import { navigate } from '../lib/router';
import { BUSINESS, type BusinessType } from '../lib/townProfile';
import { Mango } from '../ui/icons';

interface Me { email?: string; restaurants: { tenantId: string; name: string; role: string }[] }

/** /auth/callback: finish the Cognito sign-in, then go to /owner. */
export function Callback() {
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    finishSignIn().then(() => navigate('/owner', true), (e: Error) => setErr(e.message));
  }, []);
  return (
    <main className="screen center">
      {err ? <><p>{err}</p><button className="cta light" style={{ maxWidth: 260 }} onClick={() => void startSignIn()}>Sign in again</button></>
        : <div className="spinner" role="status" aria-label="Signing in" />}
    </main>
  );
}

/** /owner: sign in, pick one of your restaurants (or create the first one). */
export function Owner() {
  const [me, setMe] = useState<Me | null>(null);
  const [signedOut, setSignedOut] = useState(false);
  const [name, setName] = useState('');
  const [tables, setTables] = useState(10);
  const [business, setBusiness] = useState<BusinessType>('shop');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const load = async () => {
    const id = await idToken();
    if (!id) return setSignedOut(true);
    try {
      setMe(await api<Me>('GET', '/me', { token: id }));
    } catch (e) {
      if ((e as ApiError).status === 401) setSignedOut(true); else setErr((e as Error).message);
    }
  };
  useEffect(() => { void load(); }, []);

  const create = async () => {
    const id = await idToken();
    if (!id) return setSignedOut(true);
    setBusy(true);
    setErr(null);
    try {
      const r = await api<{ tenantId: string; name: string }>('POST', '/restaurants', { token: id, body: { name, business, tables: business === 'shop' ? tables : 0 } });
      await openRestaurant(r.tenantId, r.name);
    } catch (e) {
      setErr((e as Error).message);
      setBusy(false);
    }
  };

  return (
    <main className="screen" style={{ padding: '24px 20px', gap: 18 }}>
      <div className="brand"><div className="logo"><Mango /></div><span className="display" style={{ fontSize: 22 }}>FirstMango · เจ้าของร้าน</span></div>
      {signedOut ? (
        <section className="ticket">
          <h1 className="display" style={{ margin: 0, fontSize: 24 }}>เข้าสู่ระบบ · Sign in</h1>
          <p className="muted" style={{ margin: 0 }}>Owners sign in to run the floor, the kitchen and the menu.</p>
          <button className="cta light" onClick={() => void startSignIn()}>เข้าสู่ระบบ / สมัคร · Sign in or sign up</button>
        </section>
      ) : !me ? (
        <div className="center"><div className="spinner" role="status" aria-label="Loading" /></div>
      ) : (
        <>
          {me.restaurants.length > 0 && (
            <section className="ticket">
              <h1 className="display" style={{ margin: 0, fontSize: 22 }}>ร้านของคุณ · Your restaurants</h1>
              {me.restaurants.map((r) => (
                <button key={r.tenantId} className="cta light" onClick={() => void openRestaurant(r.tenantId, r.name).catch((e) => setErr(e.message))}>{r.name}</button>
              ))}
            </section>
          )}
          <section className="ticket">
            <h2 className="display" style={{ margin: 0, fontSize: 20 }}>{me.restaurants.length ? 'เปิดร้านใหม่ · New restaurant' : 'เริ่มต้นร้านแรก · Set up your restaurant'}</h2>
            <label className="field">ชื่อร้าน · Restaurant name
              <input value={name} maxLength={80} onChange={(e) => setName(e.target.value)} placeholder="Baan Mango" />
            </label>
            <div className="field" role="group" aria-label="Business type">ประเภทร้าน · What do you run?
              <div className="sf-grid2">
                {(Object.keys(BUSINESS) as BusinessType[]).map((t) => (
                  <button key={t} type="button" className={`sf-type${business === t ? ' on' : ''}`} aria-pressed={business === t} onClick={() => setBusiness(t)}>
                    <b>{BUSINESS[t].th} · {BUSINESS[t].en}</b><span>{BUSINESS[t].hint}</span>
                  </button>
                ))}
              </div>
            </div>
            {business === 'shop' ? (
              <label className="field">จำนวนโต๊ะ · Tables
                <input type="number" min={0} max={300} value={tables} onChange={(e) => setTables(Math.max(0, Math.min(300, Number(e.target.value) || 0)))} />
              </label>
            ) : (
              <p style={{ margin: 0, fontSize: 14 }}>ไม่ต้องจัดโต๊ะ ลูกค้าสแกน QR หน้าร้านเพื่อสั่ง รับที่ร้าน · No tables to set up: customers scan your stall QR to order and pick up at the counter.</p>
            )}
            <button className="cta light" disabled={!name.trim() || busy} onClick={() => void create()}>{busy ? 'กำลังสร้าง…' : 'สร้างร้าน · Create'}</button>
          </section>
          <button className="ghost" style={{ color: 'var(--ink)', borderColor: 'var(--ink)' }} onClick={() => void signOut()}>ออกจากระบบ · Sign out ({me.email})</button>
        </>
      )}
      {err && <div className="warn" role="alert">{err}</div>}
    </main>
  );
}
